package com.schoolconnect.app.repository.reports

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.AdoptionCriteriaType
import com.schoolconnect.app.datasource.network.request.PercentageOperator
import com.schoolconnect.app.datasource.network.request.UsageAdoptionReportRequest
import com.schoolconnect.app.datasource.network.request.UsageAdoptionRoleFilter
import com.schoolconnect.app.datasource.network.response.PerformanceCategoryResponse
import com.schoolconnect.app.datasource.network.response.UsageAdoptionReportResponse
import com.schoolconnect.app.datasource.network.request.AttendanceReportRequest
import com.schoolconnect.app.datasource.network.request.AcademicPerformanceReportRequest
import com.schoolconnect.app.datasource.network.request.CombinedPerformanceReportRequest
import com.schoolconnect.app.datasource.network.response.AttendanceReportResponse
import com.schoolconnect.app.datasource.network.response.AcademicPerformanceReportResponse
import com.schoolconnect.app.datasource.network.response.CombinedPerformanceReportResponse
import com.schoolconnect.app.util.scLog

object ReportsRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ReportsRepository"

    suspend fun generateAttendanceReport(
        request: AttendanceReportRequest
    ): Result<AttendanceReportResponse> = KtorApiClient.generateAttendanceReport(request)

    suspend fun exportAttendanceReport(
        request: AttendanceReportRequest,
        format: String
    ): Result<ByteArray> = KtorApiClient.exportAttendanceReport(request, format)

    suspend fun generateAcademicPerformanceReport(
        request: AcademicPerformanceReportRequest
    ): Result<AcademicPerformanceReportResponse> = KtorApiClient.generateAcademicPerformanceReport(request)

    suspend fun exportAcademicPerformanceReport(
        request: AcademicPerformanceReportRequest,
        format: String
    ): Result<ByteArray> = KtorApiClient.exportAcademicPerformanceReport(request, format)

    suspend fun generateCombinedPerformanceReport(
        request: CombinedPerformanceReportRequest
    ): Result<CombinedPerformanceReportResponse> = KtorApiClient.generateCombinedPerformanceReport(request)

    suspend fun exportCombinedPerformanceReport(
        request: CombinedPerformanceReportRequest,
        format: String
    ): Result<ByteArray> = KtorApiClient.exportCombinedPerformanceReport(request, format)

    suspend fun getPerformanceCategories(): Result<List<PerformanceCategoryResponse>> {
        scLog("$TAG getPerformanceCategories")
        return KtorApiClient.getPerformanceCategories()
    }

    suspend fun getUsageAdoptionReport(
        fromDate: String,
        toDate: String,
        role: String = "All",
        criteriaType: String? = null,
        operator: String? = null,
        values: List<Double>? = null,
        limit: Int = 20,
        offset: Int = 0
    ): Result<UsageAdoptionReportResponse> {
        scLog("$TAG getUsageAdoptionReport from=$fromDate to=$toDate role=$role")
        val roleFilter = when (role) {
            "School Admin" -> UsageAdoptionRoleFilter.SCHOOL_ADMIN
            "Management" -> UsageAdoptionRoleFilter.MANAGEMENT
            "Teacher" -> UsageAdoptionRoleFilter.TEACHER
            "Parent" -> UsageAdoptionRoleFilter.PARENT
            else -> UsageAdoptionRoleFilter.ALL
        }
        val criteria = if (criteriaType == "Adoption Rate") AdoptionCriteriaType.ADOPTION_RATE else null
        val op = when (operator) {
            "Less than" -> PercentageOperator.LESS_THAN
            "Greater than" -> PercentageOperator.GREATER_THAN
            "Equal to" -> PercentageOperator.EQUAL_TO
            "Between" -> PercentageOperator.BETWEEN
            else -> null
        }
        val request = UsageAdoptionReportRequest(
            fromDate = fromDate,
            toDate = toDate,
            role = roleFilter,
            criteriaType = criteria,
            percentageOperator = op,
            percentageValues = values,
            limit = limit,
            offset = offset
        )
        return KtorApiClient.generateUsageAdoptionReport(request)
    }

    suspend fun exportUsageAdoptionReport(
        fromDate: String,
        toDate: String,
        role: String = "All",
        criteriaType: String? = null,
        operator: String? = null,
        values: List<Double>? = null,
        format: String = "excel"
    ): Result<ByteArray> {
        scLog("$TAG exportUsageAdoptionReport format=$format")
        val roleFilter = when (role) {
            "School Admin" -> UsageAdoptionRoleFilter.SCHOOL_ADMIN
            "Management" -> UsageAdoptionRoleFilter.MANAGEMENT
            "Teacher" -> UsageAdoptionRoleFilter.TEACHER
            "Parent" -> UsageAdoptionRoleFilter.PARENT
            else -> UsageAdoptionRoleFilter.ALL
        }
        val criteria = if (criteriaType == "Adoption Rate") AdoptionCriteriaType.ADOPTION_RATE else null
        val op = when (operator) {
            "Less than" -> PercentageOperator.LESS_THAN
            "Greater than" -> PercentageOperator.GREATER_THAN
            "Equal to" -> PercentageOperator.EQUAL_TO
            "Between" -> PercentageOperator.BETWEEN
            else -> null
        }
        val request = UsageAdoptionReportRequest(
            fromDate = fromDate,
            toDate = toDate,
            role = roleFilter,
            criteriaType = criteria,
            percentageOperator = op,
            percentageValues = values,
            limit = 100,
            offset = 0
        )
        return KtorApiClient.exportUsageAdoptionReport(request, format)
    }
}
